// Modules
import { useState, useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

// Hooks
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// Redux
import {
	showHideFilter,
	updatedSelectedFilter,
	updatedselectedRowsPerPage,
	selectedCurrentGroup,
	selectedOutletCodeSelector,
	updateFilterInputs,
	filterInputsSelector
} from "redux/userList/userSlice";

// constants
import { Filters as FilterItems } from "constants/filters";
import { Routes } from "constants/global";

export type SelectedItem = {
	name: string;
	value: string | string[];
	checked: boolean;
};

export type useSearchFilterIconProps = {
	defaultSelectedFilterValues: Array<string>;
	defaultSelectedFilter: Array<SelectedItem>;
};

export type FilterSelectData = {
	name: string;
	value: string;
	checked: boolean;
	isTextInput?: boolean;
};

export const useSearchFilterIcon = ({ defaultSelectedFilter, defaultSelectedFilterValues }: useSearchFilterIconProps) => {
	const dispatch = useAppDispatch();
	const { pathname } = useLocation();
	const showFilter = useAppSelector(showHideFilter);
	const selectedGroup = useAppSelector(selectedCurrentGroup);
	const outletCode = useAppSelector(selectedOutletCodeSelector);
	const filterInputs = useAppSelector(filterInputsSelector);
	const [selectedFilers, setSelectedFilters] = useState<Array<SelectedItem>>(defaultSelectedFilter);
	const [selectedFilterValues, setSelectedFiltersIds] = useState<Array<string>>(defaultSelectedFilterValues);

	const filterData: { [key: string]: string } = {};
	const filterItems = useMemo(() => {
		switch (pathname) {
			case Routes.ROLE_MANAGEMENT:
				return FilterItems.ROLE_MANAGEMENT;
			case Routes.RETRO_CLAIM:
				return FilterItems.RETRO_CLAIM;
			case Routes.DECLARATION_VERIFICATION:
			case Routes.DECLARATION_APPROVAL:
				return FilterItems.REIMB_CLAIM;
			case Routes.ACCOUNT_MANAGEMENT:
				return FilterItems.ACCOUNT_MANAGEMENT;
			case Routes.MONTHLY_DECLARATIONS:
				return FilterItems.REIMB_LIST;
			default:
				return [];
		}
	}, []);

	useEffect(() => {
		filterData[selectedGroup] = selectedFilterValues[0];
		if (selectedFilterValues[1]) {
			filterData[`${selectedGroup}_sort`] = selectedFilterValues[1];
		}
		dispatch(updatedSelectedFilter(filterData));
	}, [selectedFilterValues]);

	useEffect(() => {
		const pages = [Routes.ROLE_MANAGEMENT, Routes.MONTHLY_DECLARATIONS, Routes.ACCOUNT_MANAGEMENT];
		if (pages.includes(pathname) && selectedFilterValues[1]) {
			filterData[selectedGroup] = selectedFilterValues[1];
			dispatch(updatedselectedRowsPerPage(filterData));
		}
	}, [selectedFilterValues[1]]);

	const handleTextInputFilter = useCallback(
		(name: string, value: string) => {
			dispatch(
				updateFilterInputs({
					filterType: name,
					value: value
				})
			);
		},
		[dispatch]
	);

	const handleFilterBySection = useCallback(
		(name: string, value: string, checked: boolean, currentFilters: SelectedItem | undefined) => {
			// For "Filter by", allow multiple selections (checkboxes)
			const currentValues: string[] = Array.isArray(currentFilters?.value) ? currentFilters?.value || [] : (currentFilters?.value || "").toString().split(",").filter(Boolean);

			const newValue = checked ? [...currentValues, value] : currentValues.filter((v: string) => v !== value);

			const updatedFilters = currentFilters
				? selectedFilers.map((filter) => (filter.name === name ? { ...filter, value: newValue } : filter))
				: [...selectedFilers, { name, value: newValue, checked }];

			setSelectedFilters(updatedFilters);
			setSelectedFiltersIds([newValue.join(","), selectedFilterValues[1]]);
			dispatch(updatedSelectedFilter({ [selectedGroup]: newValue.join(",") }));

			// Clear filter input when checkbox is unchecked
			if (!checked) {
				// Find the filterName from the filter items
				const filterItem = filterItems.find((section) => section.title === name)?.values.find((item) => item.value === value) as { filterName?: string } | undefined;
				if (filterItem && filterItem.filterName) {
					dispatch(
						updateFilterInputs({
							filterType: filterItem.filterName,
							value: ""
						})
					);
				}
			}
		},
		[selectedFilers, selectedFilterValues, selectedGroup, dispatch, filterItems]
	);

	const handleRadioButtonSection = useCallback(
		(name: string, value: string, checked: boolean, currentFilters: SelectedItem | undefined) => {
			const updatedFilters = currentFilters
				? selectedFilers.map((filter) => (filter.name === name ? { ...filter, value } : filter))
				: [...selectedFilers, { name, value, checked }];

			setSelectedFilters(updatedFilters);

			if (name === "Records per page") {
				setSelectedFiltersIds([selectedFilterValues[0], value]);
				dispatch(updatedselectedRowsPerPage({ [selectedGroup]: value }));
			} else if (name === "Sort by") {
				// For "Sort by" sections, store separately with _sort suffix
				setSelectedFiltersIds([selectedFilterValues[0], value]);
				dispatch(
					updatedSelectedFilter({
						[`${selectedGroup}_sort`]: value
					})
				);
			} else {
				setSelectedFiltersIds([value, selectedFilterValues[1]]);
				dispatch(updatedSelectedFilter({ [selectedGroup]: value }));
			}
		},
		[selectedFilers, selectedFilterValues, pathname, selectedGroup, dispatch]
	);

	const handleOnSelectFilter = useCallback(
		(data: FilterSelectData) => {
			const { name, value, checked, isTextInput } = data;

			if (isTextInput) {
				handleTextInputFilter(name, value);
				return;
			}

			const currentFilters = selectedFilers.find((filter) => filter.name === name);

			if (name === "Filter by") {
				handleFilterBySection(name, value, checked, currentFilters);
			} else {
				handleRadioButtonSection(name, value, checked, currentFilters);
			}
		},
		[selectedFilers, handleTextInputFilter, handleFilterBySection, handleRadioButtonSection]
	);

	// Clear selected filters
	const handleOnResetFilter = useCallback(() => {
		setSelectedFilters(defaultSelectedFilter);
		setSelectedFiltersIds(defaultSelectedFilterValues);
	}, [setSelectedFilters, setSelectedFiltersIds]);

	// Incase the Outlet is changed, reset all the filters
	useEffect(() => {
		handleOnResetFilter();
	}, [outletCode]);

	return {
		handleOnSelectFilter,
		handleOnResetFilter,
		showFilter,
		filterItems,
		selectedFilterValues,
		handleTextInputFilter,
		filterInputs
	};
};
